import type { ComponentProps } from 'react';
import { cn } from '../../common/functions/cn';

const Card = ({ className, size = 'default', ...props }: ComponentProps<'div'> & { size?: 'default' | 'sm' }) => {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(
        'group/card flex flex-col gap-(--card-spacing) overflow-hidden rounded-md bg-card py-(--card-spacing) text-card-foreground text-sm ring-1 ring-foreground/10 ring-inset',
        '[--card-spacing:--spacing(4)]',
        'has-data-[slot=card-footer]:pb-0 data-[size=sm]:has-data-[slot=card-footer]:pb-0 data-[size=sm]:[--card-spacing:--spacing(3)]',
        'has-[>img:first-child]:pt-0 *:[img:first-child]:rounded-t-md *:[img:last-child]:rounded-b-md',
        className
      )}
      {...props}
    />
  );
};

const CardHeader = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'group/card-header @container/card-header grid auto-rows-min items-start gap-1 rounded-t-md px-(--card-spacing) [.border-b]:pb-(--card-spacing)',
        'has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto]',
        className
      )}
      {...props}
    />
  );
};

const CardTitle = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="card-title"
      className={cn('font-medium text-base leading-snug group-data-[size=sm]/card:text-sm', className)}
      {...props}
    />
  );
};

const CardDescription = ({ className, ...props }: ComponentProps<'div'>) => {
  return <div data-slot="card-description" className={cn('text-muted-foreground text-sm', className)} {...props} />;
};

const CardAction = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
};

const CardContent = ({ className, ...props }: ComponentProps<'div'>) => {
  return <div data-slot="card-content" className={cn('px-(--card-spacing)', className)} {...props} />;
};

const CardFooter = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center rounded-b-md border-t p-(--card-spacing)', className)}
      {...props}
    />
  );
};

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
